import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { Article } from "@myams/shared";
import { MaBottomNav, MaChipRow, MaIcon, MaImageSlot, MaTopBar } from "../components/ui";
import { Screen } from "../components/layout/Screen";
import { useArticles } from "../lib/queries";

// Feedspot Amsterdam directory + v1 source set decided in the plan.
// Order = display order on the chip row.
const SOURCE_CHIPS = [
  { id: "all",              label: "All" },
  { id: "amsterdamfoodie",  label: "Foodie" },
  { id: "culi-amsterdam",   label: "Culi" },
  { id: "eating-europe",    label: "Eating Europe" },
  { id: "vegan-amsterdam",  label: "Vegan" },
];

function formatPublished(ts: number | null | undefined): string {
  if (!ts) return "";
  const now = Math.floor(Date.now() / 1000);
  const dt = now - ts;
  if (dt < 3600) return `${Math.max(1, Math.floor(dt / 60))}m ago`;
  if (dt < 86400) return `${Math.floor(dt / 3600)}h ago`;
  if (dt < 86400 * 7) return `${Math.floor(dt / 86400)}d ago`;
  return new Intl.DateTimeFormat("en-GB", {
    timeZone: "Europe/Amsterdam",
    day: "numeric", month: "short",
  }).format(new Date(ts * 1000));
}

export function Eat() {
  const navigate = useNavigate();
  const [source, setSource] = useState<string>("all");
  const { data: articles = [], isPending } = useArticles({ limit: 50 });

  const filtered = useMemo<Article[]>(() => {
    if (source === "all") return articles;
    return articles.filter((a) => a.source === source);
  }, [articles, source]);

  return (
    <Screen footer={<MaBottomNav active="eat" />}>
      <MaTopBar
        title="EAT!"
        subtitle={`${filtered.length} of ${articles.length} · food writing from Amsterdam`}
      />

      <MaChipRow
        items={SOURCE_CHIPS}
        active={source}
        onSelect={setSource}
      />

      {isPending && (
        <div className="ma-mono" style={{ fontSize: 11, color: "var(--ma-ink-soft)", textAlign: "center", padding: 24 }}>
          loading articles…
        </div>
      )}

      {!isPending && filtered.length === 0 && (
        <div style={{ padding: 24, textAlign: "center" }}>
          <div className="ma-display" style={{ fontSize: 22, marginBottom: 6 }}>No articles yet</div>
          <div style={{ fontFamily: "var(--ma-body)", fontSize: 14, color: "var(--ma-ink-soft)" }}>
            The next scrape runs every 6 hours. Or run one from /admin/sources.
          </div>
        </div>
      )}

      <div style={{ padding: "8px 16px 0", display: "flex", flexDirection: "column", gap: 10 }}>
        {filtered.map((a) => (
          <button
            key={a.id}
            onClick={() => navigate(`/eat/${encodeURIComponent(a.id)}`)}
            style={{
              display: "flex", gap: 12,
              background: "var(--ma-cream)",
              border: "var(--ma-border) solid var(--ma-ink)",
              borderRadius: "var(--ma-card-radius)",
              overflow: "hidden", padding: 0,
              color: "inherit", cursor: "pointer", textAlign: "left", width: "100%",
            }}>
            <div style={{ width: 100, flexShrink: 0 }}>
              <MaImageSlot height="100%" tone="mustard" src={a.imageUrl ?? null} />
            </div>
            <div style={{ flex: 1, padding: "12px 14px 12px 0", minWidth: 0 }}>
              <div className="ma-mono" style={{
                fontSize: 9, letterSpacing: "0.14em",
                textTransform: "uppercase", color: "var(--ma-brick)", marginBottom: 4,
              }}>
                {a.source}{a.publishedAt ? ` · ${formatPublished(a.publishedAt)}` : ""}
              </div>
              <div className="ma-display" style={{ fontSize: 17, color: "var(--ma-ink)", lineHeight: 1.15, marginBottom: 4,
                display: "-webkit-box", WebkitBoxOrient: "vertical", WebkitLineClamp: 2, overflow: "hidden" }}>
                {a.title}
              </div>
              {a.excerpt && (
                <div style={{
                  fontFamily: "var(--ma-body)", fontSize: 12,
                  color: "var(--ma-ink-soft)", lineHeight: 1.35,
                  display: "-webkit-box", WebkitBoxOrient: "vertical", WebkitLineClamp: 2, overflow: "hidden",
                }}>
                  {a.excerpt}
                </div>
              )}
            </div>
            <div style={{ alignSelf: "center", padding: "0 12px" }}>
              <MaIcon name="arrow" size={14} stroke={1.6} color="var(--ma-ink-soft)" />
            </div>
          </button>
        ))}
      </div>
    </Screen>
  );
}
